/**
 * LocationsList — listado de Ubicaciones.
 *
 * Es la PRIMERA lista migrada al estándar (`components/common/ListPage.jsx`):
 * buscador, filtros, exportar a Excel, columnas mostrar/ocultar, cebra de
 * filas, clic en la fila para abrir la ficha en lectura y botones
 * [acciones propias] · Copiar · Editar · Eliminar son comunes.
 *
 * Lo único propio de esta pantalla: el árbol de la columna Nombre, los filtros
 * de tipo/estado/padre/raíces, las dos acciones específicas (ver racks y mover
 * de padre) y el aviso de qué se copia y qué no.
 *
 * Reglas del estándar (ver `docs/ESTANDAR_LISTADOS.md`):
 *  · las clases son `list-*`, sin CSS propio;
 *  · la ficha SIEMPRE abre en modo lectura (se edita con el botón «Editar»).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MapPinIcon,
  RectangleStackIcon,
  ArrowRightCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ListBulletIcon,
  Squares2X2Icon,
  QueueListIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import CloneDialog from './common/CloneDialog';
import LocationDeleteInfo from './LocationDeleteInfo';
import { useListView } from '../views/ViewsContext';
import LocationForm from './LocationForm';
import { useAutoOpen } from '../hooks/useAutoOpen';
import useNavOrder from '../hooks/useNavOrder';
import api from '../services/api';
import { buildTreeFlat, subtreeIds, typeIcon, typeLabel, TYPE_LABELS } from '../utils/locationTree';
import { getServerFormStyle } from '../utils/serverFormStyle';

function LocationsList({ companyId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);

  const [locations, setLocations] = useState([]);
  const [estiloFicha] = useState(getServerFormStyle);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('view');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationToCopy, setLocationToCopy] = useState(null);
  const [locationToMove, setLocationToMove] = useState(null);
  const [moveParentId, setMoveParentId] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterParent, setFilterParent] = useState('');
  const [onlyRoots, setOnlyRoots] = useState('');
  const [sort, setSort] = useState(null);
  // Nodos plegados del árbol (solo afecta a la vista «Árbol»).
  const [collapsed, setCollapsed] = useState(() => new Set());

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError('');
      if (!companyId) {
        setError(tr('msg.select_company', 'Selecciona una empresa para ver ubicaciones.'));
        setLocations([]);
        return;
      }
      // Con las inactivas incluidas: el filtro de Estado necesita verlas (borrar
      // una ubicación es lógico: la marca como inactiva, no la destruye).
      const response = await api.getLocations('', companyId, { includeInactive: true });
      setLocations(response.data || []);
    } catch (err) {
      setError(err.message);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  /* ── Apertura de la ficha: lectura por defecto ──────────────────────── */
  const handleOpen = (location) => {
    setSelectedLocation(location);
    setFormMode('view');
    setShowForm(true);
  };
  const handleEdit = (location) => {
    setSelectedLocation(location);
    setFormMode('edit');
    setShowForm(true);
  };
  const handleCreate = () => {
    setSelectedLocation(null);
    setFormMode('edit');
    setShowForm(true);
  };
  useAutoOpen(locations, handleOpen);

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedLocation(null);
    fetchLocations();
  };

  /* ── Borrado y desactivación ────────────────────────────────────────── */
  // Borrado FÍSICO: la ubicación desaparece y sus elementos se quedan sin
  // ubicación (el diálogo lo explica con el recuento real del backend).
  const handleDelete = async (location) => {
    try {
      const response = await api.deleteLocation(location.id, companyId);
      if (response && response.success === false) {
        throw new Error(response.message || 'No se pudo eliminar');
      }
      await fetchLocations();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la ubicación');
    }
  };

  // Alternativa conservadora: se queda la fila, marcada como inactiva.
  const handleDeactivate = async (location) => {
    try {
      await api.deleteLocation(location.id, companyId, { mode: 'deactivate' });
      await fetchLocations();
    } catch (err) {
      setError(err.message || 'No se pudo desactivar la ubicación');
    }
  };

  /** Alcance del borrado para el diálogo (CIs sin ubicación, hijas, notas…). */
  const prepareDelete = async (location) => {
    const response = await api.getLocationDeletePreview(location.id, companyId);
    return response?.data || null;
  };

  /* ── Copiar ─────────────────────────────────────────────────────────── */
  const handleCopy = async (name) => {
    setBusy(true);
    setDialogError('');
    try {
      await api.cloneLocation(locationToCopy.id, { name }, companyId);
      setLocationToCopy(null);
      await fetchLocations();
    } catch (err) {
      setDialogError(err.message || 'No se pudo copiar la ubicación');
    } finally {
      setBusy(false);
    }
  };

  /* ── Mover a otro padre ─────────────────────────────────────────────── */
  const handleMove = async () => {
    setBusy(true);
    setDialogError('');
    try {
      await api.updateLocation(
        locationToMove.id,
        { parent_id: moveParentId ? parseInt(moveParentId, 10) : null },
        companyId,
      );
      setLocationToMove(null);
      setMoveParentId('');
      await fetchLocations();
    } catch (err) {
      setDialogError(err.message || 'No se pudo mover la ubicación');
    } finally {
      setBusy(false);
    }
  };

  /* ── Datos derivados ────────────────────────────────────────────────── */
  const byId = useMemo(() => {
    const map = new Map();
    locations.forEach((l) => map.set(l.id, l));
    return map;
  }, [locations]);

  const parentName = useCallback(
    (loc) => byId.get(loc.parent_id)?.name || '',
    [byId],
  );

  /** Filas: árbol por defecto; si el usuario ordena por columna, lista plana. */
  const orderedRows = useMemo(
    () => (sort ? locations.map((l) => ({ ...l, _depth: -1 })) : buildTreeFlat(locations)),
    [locations, sort],
  );

  /* ── Vista activa (declarada aquí; el conmutador es común) ───────────── */
  const LOCATION_VIEWS = useMemo(() => [
    { key: 'tree', label: tr('location.view_tree', 'Árbol'), icon: QueueListIcon, title: tr('location.view_tree_hint', 'Jerarquía plegable: edificio > planta > sala > fila') },
    { key: 'list', label: tr('location.view_list', 'Lista'), icon: ListBulletIcon, title: tr('location.view_list_hint', 'Tabla plana, sin jerarquía') },
    { key: 'card', label: tr('location.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('location.view_cards_hint', 'Tarjetas con la información principal'), render: renderLocationCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);
  const locationView = useListView('locations', 'tree').view;
  const isTree = locationView === 'tree';

  /** Un nodo tiene hijos si alguna otra ubicación lo tiene como padre. */
  const hasChildren = useMemo(() => {
    const parents = new Set(locations.map((l) => l.parent_id).filter((id) => id != null));
    return (loc) => parents.has(loc.id);
  }, [locations]);

  /**
   * Filas del árbol escondiendo las ramas plegadas. Se recorre en profundidad,
   * así que un nodo posterior con más profundidad que el plegado es su hijo.
   */
  const treeRows = useMemo(() => {
    const out = [];
    let hideBelow = null;
    orderedRows.forEach((loc) => {
      if (hideBelow !== null) {
        if ((loc._depth ?? 0) > hideBelow) return;
        hideBelow = null;
      }
      out.push(loc);
      if (collapsed.has(loc.id) && hasChildren(loc)) hideBelow = loc._depth ?? 0;
    });
    return out;
  }, [orderedRows, collapsed, hasChildren]);

  const toggleCollapsed = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const filteredLocations = useMemo(() => {
    const term = search.trim().toLowerCase();
    const base = sort ? orderedRows : treeRows;
    return base.filter((loc) => {
      if (term) {
        const haystack = [
          loc.name, loc.code, loc.description, typeLabel(loc.location_type), parentName(loc),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (filterType && (loc.location_type || '') !== filterType) return false;
      if (filterStatus === 'active' && !loc.is_active) return false;
      if (filterStatus === 'inactive' && loc.is_active) return false;
      if (filterParent && String(loc.parent_id ?? '') !== filterParent) return false;
      if (onlyRoots && loc.parent_id != null) return false;
      return true;
    });
  }, [orderedRows, treeRows, sort, search, filterType, filterStatus, filterParent, onlyRoots, parentName]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navLocations = useNavOrder(filteredLocations, selectedLocation, showForm);

  const activeCount = locations.filter((l) => l.is_active).length;
  const inactiveCount = locations.length - activeCount;

  const exportRows = useMemo(() => filteredLocations.map((loc) => ({
    Nombre: `${'  '.repeat(Math.max(0, loc._depth || 0))}${loc.name || ''}`,
    Nivel: (loc._depth ?? -1) >= 0 ? loc._depth + 1 : '',
    Código: loc.code || '',
    Tipo: typeLabel(loc.location_type),
    Padre: parentName(loc),
    Estado: loc.is_active ? 'Activa' : 'Inactiva',
    Dirección: loc.address || '',
    Descripción: loc.description || '',
  })), [filteredLocations, parentName]);

  /** Número de sub-ubicaciones directas (para las fichas). */
  const childrenCount = useMemo(() => {
    const counts = new Map();
    locations.forEach((l) => {
      if (l.parent_id != null) counts.set(l.parent_id, (counts.get(l.parent_id) || 0) + 1);
    });
    return (loc) => counts.get(loc.id) || 0;
  }, [locations]);

  /* ── Columnas ───────────────────────────────────────────────────────── */
  const columns = useMemo(() => [
    {
      key: 'name',
      label: tr('location.name', 'Nombre'),
      sortable: true,
      sortValue: (loc) => loc.name,
      render: (loc) => {
        // La indentación y el plegado son de la vista «Árbol»; en «Lista» la
        // tabla es plana a propósito (comparar nombres sin jerarquía de por medio).
        const depth = isTree ? Math.max(0, loc._depth || 0) : 0;
        const foldable = isTree && hasChildren(loc);
        const isCollapsed = collapsed.has(loc.id);
        return (
          <span style={{ paddingLeft: depth * 18, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {isTree && (foldable ? (
              <button
                type="button"
                className="list-tree-toggle"
                onClick={(event) => { event.stopPropagation(); toggleCollapsed(loc.id); }}
                title={isCollapsed
                  ? tr('location.expand', 'Desplegar sub-ubicaciones')
                  : tr('location.collapse', 'Plegar sub-ubicaciones')}
                aria-expanded={!isCollapsed}
              >
                {isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
              </button>
            ) : (
              <span className="list-tree-spacer" aria-hidden="true" />
            ))}
            <span aria-hidden="true">{typeIcon(loc.location_type)}</span>
            {loc.name}
          </span>
        );
      },
    }, {
      key: 'code',
      label: tr('location.code', 'Código'),
      sortable: true,
      render: (loc) => (loc.code ? <span className="list-code">{loc.code}</span> : <span className="list-hint">—</span>),
    },
    {
      key: 'location_type',
      label: tr('location.type', 'Tipo'),
      sortable: true,
      sortValue: (loc) => typeLabel(loc.location_type),
      render: (loc) => (
        <span>{typeIcon(loc.location_type)} {typeLabel(loc.location_type)}</span>
      ),
    },
    {
      key: 'parent',
      label: tr('location.parent', 'Padre'),
      sortable: true,
      sortValue: (loc) => parentName(loc),
      render: (loc) => {
        const parent = byId.get(loc.parent_id);
        if (!parent) return <span className="list-hint">—</span>;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)', fontSize: '0.85em' }}>
            <span aria-hidden="true">{typeIcon(parent.location_type)}</span>
            {parent.name}
          </span>
        );
      },
    },
    {
      key: 'description',
      label: tr('location.description', 'Descripción'),
      render: (loc) => loc.description || <span className="list-hint">—</span>,
    },
    {
      key: 'is_active',
      label: tr('location.status', 'Estado'),
      sortable: true,
      sortValue: (loc) => (loc.is_active ? 1 : 0),
      render: (loc) => (
        <span className={`list-badge ${loc.is_active ? 'list-badge--on' : 'list-badge--off'}`}>
          {loc.is_active ? tr('location.active', 'Activa') : tr('location.inactive', 'Inactiva')}
        </span>
      ),
    },
  ], [byId, tr, parentName, isTree, collapsed, hasChildren, toggleCollapsed]);

  /* ── Filtros ────────────────────────────────────────────────────────── */
  const filters = [
    {
      key: 'type',
      label: tr('location.filter_type', 'Tipo'),
      allLabel: tr('location.filter_all_types', 'Todos los tipos'),
      value: filterType,
      onChange: setFilterType,
      options: Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'status',
      label: tr('location.filter_status', 'Estado'),
      allLabel: tr('location.filter_all_states', 'Todos los estados'),
      value: filterStatus,
      onChange: setFilterStatus,
      options: [
        { value: 'active', label: tr('location.filter_active', 'Activas') },
        { value: 'inactive', label: tr('location.filter_inactive', 'Inactivas') },
      ],
    },
    {
      key: 'parent',
      label: tr('location.parent', 'Padre'),
      allLabel: tr('location.filter_any_parent', 'Cualquier padre'),
      value: filterParent,
      onChange: setFilterParent,
      options: locations
        .filter((l) => l.is_active)
        .slice()
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }))
        .map((l) => ({
          value: String(l.id),
          label: `${typeIcon(l.location_type)} ${l.name}`,
        })),
    },
    {
      key: 'roots',
      type: 'toggle',
      label: tr('location.only_roots', 'Solo raíces'),
      title: tr('location.only_roots_hint', 'Mostrar solo las ubicaciones sin padre'),
      value: onlyRoots,
      onValue: 'roots',
      onChange: setOnlyRoots,
    },
  ];

  const subtitle = `${locations.length} ${tr('location.title', 'Ubicaciones').toLowerCase()}`
    + ` · ${filteredLocations.length} ${tr('location.filtered', 'filtradas')}`
    + (inactiveCount ? ` · ${inactiveCount} ${tr('location.inactive_plural', 'inactivas')}` : '');

  /* Padres válidos al mover: ni ella misma ni sus descendientes. */
  const moveCandidates = useMemo(() => {
    if (!locationToMove) return [];
    const forbidden = subtreeIds(locations, locationToMove.id);
    return locations
      .filter((l) => l.is_active && !forbidden.has(l.id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));
  }, [locations, locationToMove]);

  /** Vista de fichas: tarjetas con lo esencial y los mismos botones de fila. */
  function renderLocationCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((loc) => {
          const parent = byId.get(loc.parent_id);
          const kids = childrenCount(loc);
          return (
            <div
              key={loc.id}
              className="list-card"
              role="button"
              tabIndex={0}
              onClick={() => handleOpen(loc)}
              onKeyDown={(event) => { if (event.key === 'Enter') handleOpen(loc); }}
              title={`${tr('action.view', 'Ver')} ${loc.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">
                    <span aria-hidden="true">{typeIcon(loc.location_type)}</span> {loc.name}
                  </div>
                  {loc.code && <span className="list-code">{loc.code}</span>}
                </div>
                <span className={`list-badge ${loc.is_active ? 'list-badge--on' : 'list-badge--off'}`}>
                  {loc.is_active ? tr('location.active', 'Activa') : tr('location.inactive', 'Inactiva')}
                </span>
              </div>

              <div className="list-card-meta">
                <span>{typeLabel(loc.location_type)}</span>
                {parent && <span>{tr('location.parent', 'Padre')}: {parent.name}</span>}
                {kids > 0 && (
                  <span>
                    {kids} {tr('location.children', 'sub-ubicaciones')}
                  </span>
                )}
              </div>

              {loc.description && <div className="list-hint">{loc.description}</div>}

              <div className="list-card-actions">{rowActions(loc)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <>
      {showForm && (
        <LocationForm
          location={selectedLocation}
          companyId={companyId}
          initialMode={formMode}
          variant={estiloFicha}
          // Contador de fichas: el orden del listado tal y como se ve, congelado.
          siblingLocations={navLocations}
          onNavigate={(destino) => setSelectedLocation(destino)}
          onClose={handleFormClose}
          onSave={(creado) => {
            fetchLocations();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setSelectedLocation(creado); setFormMode('edit'); }
          }}
        />
      )}

      {/* La ficha (`dv-overlay`) tapa el listado; no se desmonta para no
          volver a pedir los datos al cerrarla. */}
      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={MapPinIcon}
          title={tr('location.title', 'Ubicaciones')}
          subtitle={subtitle}
          onCreate={handleCreate}
          createLabel={tr('location.new', 'Nueva ubicación')}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('location.search_placeholder', 'Buscar por nombre, código, tipo o padre…')}
          filters={filters}
          exportRows={exportRows}
          exportFileBase="ubicaciones"
          exportSheetName="Ubicaciones"

          columns={columns}
          columnStorageKey="cmdb_cols_locations"
          companyId={companyId}
          importEntity="locations"
          onImported={() => fetchLocations()}
          rows={filteredLocations}
          rowKey={(loc) => loc.id}
          onRowClick={handleOpen}
          onSortChange={setSort}
          rowTitle={(loc) => `${tr('action.view', 'Ver')} ${loc.name}`}

          views={LOCATION_VIEWS}
          listKey="locations"
          defaultView="tree"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando ubicaciones…')}
          error={error}

          empty={{
            icon: '📍',
            title: locations.length === 0
              ? tr('msg.no_data', 'No hay ubicaciones todavía')
              : tr('msg.no_results', 'Ninguna ubicación coincide con los filtros'),
            subtitle: locations.length === 0
              ? tr('location.empty_hint', 'Crea la primera para empezar.')
              : undefined,
            action: locations.length === 0 && companyId ? (
              <button type="button" className="list-btn-primary" onClick={handleCreate}>
                ＋ {tr('location.new', 'Nueva ubicación')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (loc) => (
              <>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => navigate(`/racks/${loc.id}`)}
                  title={tr('location.view_racks', 'Ver racks')}
                >
                  <RectangleStackIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => {
                    setDialogError('');
                    setMoveParentId(loc.parent_id != null ? String(loc.parent_id) : '');
                    setLocationToMove(loc);
                  }}
                  title={tr('location.move', 'Mover a otra ubicación padre')}
                >
                  <ArrowRightCircleIcon />
                </button>
              </>
            ),
            onCopy: (loc) => { setDialogError(''); setLocationToCopy(loc); },
            onEdit: handleEdit,
            onDelete: handleDelete,
            deleteTitle: tr('location.delete_title', 'Eliminar ubicación'),
            deleteMessage: (loc) => (
              <>
                {tr('location.delete_confirm_hard', '¿Seguro que quieres eliminar definitivamente la ubicación')}{' '}
                <strong>{loc.name}</strong>?{' '}
                {tr('location.delete_hint_hard', 'No se puede deshacer.')}
              </>
            ),
            prepareDelete,
            deleteDetails: (info) => <LocationDeleteInfo info={info} />,
            // Desactivar es la alternativa suave: conserva la ficha y sus
            // elementos, y se puede revertir.
            onDeactivate: handleDeactivate,
            canDeactivate: (loc) => loc.is_active,
            deactivateLabel: tr('location.deactivate', 'Desactivar'),
            deactivateTitle: tr('location.deactivate_hint', 'Dejará de aparecer en los listados y selectores, pero se conserva y se puede volver a activar.'),
          }}
        />
      </div>

      {/* Diálogo: copiar ubicación */}
      {locationToCopy && (
        <CloneDialog
          title={tr('location.copy_title', 'Copiar ubicación')}
          nameLabel={tr('location.copy_name', 'Nombre de la copia')}
          placeholder={tr('location.copy_placeholder', 'Nombre de la nueva ubicación…')}
          defaultName={`${locationToCopy.name} (copia)`}
          hint={tr(
            'location.copy_hint',
            'Se copiará la configuración (tipo, ubicación padre, dirección, edificio/planta y notas). No se copiará el código ni el plano de planta; los racks siguen siendo de la ubicación original.',
          )}
          busy={busy}
          error={dialogError}
          onClose={() => { setLocationToCopy(null); setDialogError(''); }}
          onConfirm={handleCopy}
        />
      )}

      {/* Diálogo: mover a otra ubicación padre */}
      {locationToMove && (
        <Dialog
          title={tr('location.move_title', 'Mover a otra ubicación padre')}
          icon="↪"
          busy={busy}
          onClose={() => { setLocationToMove(null); setDialogError(''); }}
          footer={(
            <>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => { setLocationToMove(null); setDialogError(''); }}
                disabled={busy}
              >
                {tr('action.cancel', 'Cancelar')}
              </button>
              <button type="button" className="list-btn-primary" onClick={handleMove} disabled={busy}>
                {busy ? tr('action.saving', 'Guardando…') : tr('location.move', 'Mover')}
              </button>
            </>
          )}
        >
          <p>
            {tr('location.move_text', 'Nueva ubicación padre de')} <strong>{locationToMove.name}</strong>
          </p>
          <select
            className="list-select"
            style={{ width: '100%', minWidth: 0 }}
            value={moveParentId}
            onChange={(e) => setMoveParentId(e.target.value)}
          >
            <option value="">{tr('location.move_to_root', '— Sin padre (raíz) —')}</option>
            {moveCandidates.map((loc) => (
              <option key={loc.id} value={String(loc.id)}>
                {typeIcon(loc.location_type)} {loc.name}{loc.code ? ` (${loc.code})` : ''}
              </option>
            ))}
          </select>
          {dialogError && <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">{dialogError}</div>}
        </Dialog>
      )}
    </>
  );
}

export default LocationsList;
